import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { finalize } from "rxjs/operators";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Riesgos } from "src/app/shared/model/gestion-proyectos/riesgos/riesgo.model";
import { Constantes } from "src/app/utilerias/constantes/constantes";
import { ModalSeguimientoRiesgoComponent } from "src/app/proyectos/admin-proyectos/modal-seguimiento-riesgo/modal-seguimiento-riesgo.component";
import { ProyectosService } from "src/app/proyectos/proyectos.service";

interface RiesgoProyectoItem {
  id: number;
  fecha: string;
  descripcion: string;
  detalle: string;
  probabilidad: string;
  impacto: string;
  severidad: string;
  severidadColor: string;
  severidadScore: number;
  estrategia: string;
  accionesSeguir: string;
  periodicidad: string;
  responsable: string;
  estado: "Mitigado" | "Activo";
  riesgoData: Riesgos;
}

interface RiesgosProyectoDialogData {
  projectId: string;
  projectTitle: string;
  riskCount: number;
  projectClient?: string;
  projectLeader?: string;
}

interface SeverityDistributionItem {
  key: "critico" | "alto" | "medio" | "bajo";
  label: string;
  count: number;
  percent: number;
  color: string;
}

interface SeverityChartItem extends SeverityDistributionItem {
  shortLabel: string;
  height: number;
}

@Component({
  selector: "app-riesgos-proyecto",
  standalone: true,
  templateUrl: "./riesgos-proyecto.component.html",
  styleUrls: ["./riesgos-proyecto.component.css"],
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule],
})
export class RiesgosProyectoComponent implements OnInit {
  readonly projectTitle: string;
  readonly projectId: string;
  readonly projectClient: string;
  readonly projectLeader: string;
  readonly searchPlaceholder = "Buscar riesgo...";
  readonly filterOptions = ["Todos", "Activo", "Mitigado", "Crítico"];

  searchTerm = "";
  selectedFilter = "Todos";
  isLoading = false;
  loadError = false;
  riesgos: RiesgoProyectoItem[] = [];

  private readonly consultaObject = new ConsultaList<Riesgos>();

  constructor(
    public dialogRef: MatDialogRef<RiesgosProyectoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RiesgosProyectoDialogData,
    private proyectoService: ProyectosService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    this.projectId = data?.projectId ?? "";
    this.projectTitle = data?.projectTitle ?? "Proyecto";
    this.projectClient = data?.projectClient ?? "Sin cliente";
    this.projectLeader = data?.projectLeader ?? "Sin líder";
  }

  ngOnInit(): void {
    this.consultarRiesgos();
  }

  close(): void {
    this.dialogRef.close();
  }

  get riesgosFiltrados(): RiesgoProyectoItem[] {
    const search = this.searchTerm.trim().toLowerCase();

    return this.riesgos.filter((riesgo) => {
      const matchesSearch =
        !search ||
        riesgo.descripcion.toLowerCase().includes(search) ||
        riesgo.detalle.toLowerCase().includes(search) ||
        riesgo.estrategia.toLowerCase().includes(search) ||
        riesgo.responsable.toLowerCase().includes(search);

      const matchesFilter =
        this.selectedFilter === "Todos" ||
        (this.selectedFilter === "Crítico" && this.resolveSeverityBucket(riesgo.severidad) === "critico") ||
        riesgo.estado === this.selectedFilter;

      return matchesSearch && matchesFilter;
    });
  }

  get displayedRiesgos(): RiesgoProyectoItem[] {
    const hasSearch = this.searchTerm.trim().length > 0;
    const hasFilter = this.selectedFilter !== "Todos";

    return hasSearch || hasFilter ? this.riesgosFiltrados : this.riesgos;
  }

  get riesgosActivos(): number {
    return this.riesgos.filter((riesgo) => riesgo.estado === "Activo").length;
  }

  get riesgosMitigados(): number {
    return this.riesgos.filter((riesgo) => riesgo.estado === "Mitigado").length;
  }

  get riesgosAbiertos(): number {
    return this.riesgosActivos;
  }

  get exposicionTotal(): number {
    return this.riesgos
      .filter((riesgo) => riesgo.estado === "Activo")
      .reduce((total, riesgo) => total + (Number(riesgo.severidadScore) || 0), 0);
  }

  get totalRiesgos(): number {
    return this.riesgos.length;
  }

  get severityDistribution(): SeverityDistributionItem[] {
    const total = this.totalRiesgos;
    const counts: Record<SeverityDistributionItem["key"], number> = {
      critico: 0,
      alto: 0,
      medio: 0,
      bajo: 0,
    };

    this.riesgos.forEach((riesgo) => {
      counts[this.resolveSeverityBucket(riesgo.severidad)] += 1;
    });

    return [
      {
        key: "critico",
        label: "Crítico",
        count: counts.critico,
        percent: this.toPercent(counts.critico, total),
        color: "#d62d2d",
      },
      {
        key: "alto",
        label: "Alto",
        count: counts.alto,
        percent: this.toPercent(counts.alto, total),
        color: "#f97316",
      },
      {
        key: "medio",
        label: "Medio",
        count: counts.medio,
        percent: this.toPercent(counts.medio, total),
        color: "#eab308",
      },
      {
        key: "bajo",
        label: "Bajo",
        count: counts.bajo,
        percent: this.toPercent(counts.bajo, total),
        color: "#0f766e",
      },
    ];
  }

  get heroChartStyle(): Record<string, string> {
    const distribution = this.severityDistribution;
    const hasData = distribution.some((item) => item.count > 0);

    if (!hasData) {
      return {
        background:
          "radial-gradient(circle at center, #ffffff 58%, transparent 59%), conic-gradient(#dbe3ee 0deg 360deg)",
      };
    }

    let currentAngle = 0;
    const segments = distribution
      .filter((item) => item.count > 0)
      .map((item) => {
        const nextAngle = currentAngle + item.percent * 3.6;
        const segment = `${item.color} ${currentAngle}deg ${nextAngle}deg`;
        currentAngle = nextAngle;
        return segment;
      })
      .join(", ");

    return {
      background: `radial-gradient(circle at center, #ffffff 58%, transparent 59%), conic-gradient(${segments})`,
    };
  }

  get severityChartItems(): SeverityChartItem[] {
    const items = this.severityDistribution
      .slice()
      .reverse()
      .map((item) => ({
        ...item,
        shortLabel: this.resolveShortSeverityLabel(item.key),
        height: this.resolveSeverityChartHeight(item.percent, item.count),
      }));

    return items;
  }

  getImpactClass(value: string): string {
    return `risk-impact ${this.resolveToneClass(value)}`;
  }

  getSeverityStyle(color: string | undefined): Record<string, string> {
    void color;
    return {
      color: "#5f6b7a",
    };
  }

  getStatusClass(value: RiesgoProyectoItem["estado"]): string {
    return value === "Mitigado"
      ? "risk-status risk-status--mitigado"
      : "risk-status risk-status--activo";
  }

  getResponsableInitials(value: string): string {
    const words = (value || "")
      .split(" ")
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, 2);

    if (!words.length) {
      return "--";
    }

    return words.map((word) => word.charAt(0).toUpperCase()).join("");
  }

  openRiskFollowUp(riesgo: RiesgoProyectoItem): void {
    if (!riesgo?.riesgoData) {
      return;
    }

    const dialogRef = this.dialog.open(ModalSeguimientoRiesgoComponent, {
      disableClose: true,
      autoFocus: true,
      maxHeight: "900px",
      width: "1200px",
      panelClass: "custom-modalbox",
      data: { riesgo: riesgo.riesgoData },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultarRiesgos();
    });
  }

  private consultarRiesgos(): void {
    if (!this.projectId) {
      this.riesgos = [];
      return;
    }

    this.isLoading = true;
    this.loadError = false;

    this.consultaObject.regInicial = 0;
    this.consultaObject.regXPagina = Math.max(this.data?.riskCount ?? 0, 50);
    this.consultaObject.bean = new Riesgos();
    this.consultaObject.bean.id_proyecto = this.projectId;
    this.consultaObject.param1 = "proyecto";
    this.consultaObject.sort = "id";
    this.consultaObject.param4 = Constantes.UNO;

    this.proyectoService
      .consultaRiesgoPaginacion(this.consultaObject)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response) => {
          const riesgos = Array.isArray(response?.list) ? response.list : [];
          this.riesgos = riesgos.map((riesgo) => this.mapearRiesgo(riesgo));
        },
        error: () => {
          this.riesgos = [];
          this.loadError = true;
        },
      });
  }

  private mapearRiesgo(riesgo: Riesgos): RiesgoProyectoItem {
    return {
      id: riesgo.id,
      fecha: this.formatFecha(riesgo.fecha_identificacion),
      descripcion: riesgo.descripcion || `Riesgo #${riesgo.id}`,
      detalle: riesgo.causa_raiz || "Sin detalle registrado",
      probabilidad: this.resolveProbabilidad(riesgo),
      impacto: this.resolveImpacto(riesgo),
      severidad: this.resolveSeveridad(riesgo),
      severidadColor: this.normalizeHexColor(riesgo?.severidad?.color) || "#1c9da4",
      severidadScore: Number(riesgo?.calculo_severidad ?? 0),
      estrategia: this.resolveEstrategia(riesgo),
      accionesSeguir: this.resolveAccionesSeguir(riesgo.acciones_seguir),
      periodicidad: this.resolvePeriodicidad(riesgo),
      responsable: riesgo.responsable_recurso || "Sin responsable",
      estado: riesgo.estatus ? "Mitigado" : "Activo",
      riesgoData: riesgo,
    };
  }

  private formatFecha(value: Date | undefined): string {
    if (!value) {
      return "--/--/--";
    }

    const fecha = new Date(value);
    if (Number.isNaN(fecha.getTime())) {
      return "--/--/--";
    }

    return fecha.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  }

  private resolveProbabilidad(riesgo: Riesgos): string {
    return (riesgo?.probabilidad?.nombre || "N/A").toUpperCase();
  }

  private resolveImpacto(riesgo: Riesgos): string {
    return (riesgo?.impacto?.descripcion || "N/A").toUpperCase();
  }

  private resolveSeveridad(riesgo: Riesgos): string {
    const severidad = (riesgo?.severidad?.descripcion || "").toUpperCase();
    if (severidad) {
      return severidad;
    }

    const calculo = Number(riesgo?.calculo_severidad ?? 0);
    if (calculo >= 8) return "ALTA";
    if (calculo >= 4) return "MEDIA";
    return "BAJA";
  }

  private resolveEstrategia(riesgo: Riesgos): string {
    const estrategia = (riesgo?.estrategia?.estrategia || "").trim();
    return estrategia || "Sin estrategia definida";
  }

  private resolvePeriodicidad(riesgo: Riesgos): string {
    return riesgo?.periodicidad?.descripcion || "Sin periodicidad";
  }

  private resolveAccionesSeguir(value: string | undefined): string {
    const acciones = (value || "").trim();
    return acciones || "Sin acciones de seguimiento";
  }

  private toPercent(value: number, total: number): number {
    if (!total) {
      return 0;
    }

    return Math.round((value / total) * 100);
  }

  private resolveSeverityBucket(value: string): SeverityDistributionItem["key"] {
    const normalized = (value || "").toUpperCase();

    if (normalized.includes("CRIT")) {
      return "critico";
    }

    if (normalized.includes("ALTA") || normalized.includes("ALTO")) {
      return "alto";
    }

    if (normalized.includes("MEDIA") || normalized.includes("MEDIO")) {
      return "medio";
    }

    return "bajo";
  }

  private resolveShortSeverityLabel(value: SeverityDistributionItem["key"]): string {
    if (value === "bajo") {
      return "Bajo";
    }

    if (value === "medio") {
      return "Medio";
    }

    if (value === "alto") {
      return "Alto";
    }

    return "Crítico";
  }

  private resolveSeverityChartHeight(percent: number, count: number): number {
    if (!count) {
      return 12;
    }

    return Math.max(18, Math.min(56, Math.round((percent / 100) * 56)));
  }

  private resolveToneClass(value: string): string {
    const normalized = (value || "").toUpperCase();

    if (
      normalized.includes("CRIT") ||
      normalized.includes("ALTA") ||
      normalized.includes("MUY ALTO") ||
      normalized.includes("CATASTRÓFICO") ||
      normalized.includes("CATASTROFICO")
    ) {
      return "risk-tone--critico";
    }

    if (
      normalized.includes("MEDIA") ||
      normalized.includes("ALTO") ||
      normalized.includes("SERIO")
    ) {
      return "risk-tone--alto";
    }

    if (normalized.includes("MODERADO")) {
      return "risk-tone--medio";
    }

    if (normalized.includes("BAJO") || normalized.includes("INSIGNIFICANTE")) {
      return "risk-tone--bajo";
    }

    return "risk-tone--medio";
  }

  private normalizeHexColor(value: string | undefined): string | null {
    const color = (value || "").trim();
    if (!color) {
      return null;
    }

    if (/^#[0-9a-fA-F]{6}$/.test(color)) {
      return color;
    }

    if (/^[0-9a-fA-F]{6}$/.test(color)) {
      return `#${color}`;
    }

    return null;
  }
}
